/**
 * FeatureLock (iter52)
 *
 * Wraps cualquier elemento (botón, link, sección) y lo deshabilita con
 * candado + tooltip si el plan no incluye `feature`. Al hacer click,
 * abre el modal global de upgrade.
 *
 * Uso:
 *   <FeatureLock feature="f14">
 *     <Button>Generar F-14</Button>
 *   </FeatureLock>
 */
import { cloneElement, isValidElement } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import { Lock } from "lucide-react";

const FEATURE_LABELS = {
  pdf_reader: "Pro",
  auto_purchase_classification: "Pro",
  iva_auto: "Pro",
  f14: "Pro",
  anexos: "Pro",
  accounting_basic: "Pro",
  accounting_full: "Empresarial",
  multi_company: "Empresarial",
  roles: "Empresarial",
  period_close: "Empresarial",
  audit_logs: "Empresarial",
  backup: "Empresarial",
  accountant_panel: "Empresarial",
  bulk_exports: "Empresarial",
  export_full: "Pro",
  export_basic: "Pro",
  sales_book_full: "Pro",
  purchase_book_manual: "Pro",
  dashboard: "Pro",
  inventory: "Pro",
};

export default function FeatureLock({
  feature,
  children,
  showLockIcon = true,
  className = "",
}) {
  const { hasFeature, sub, setUpgradeFor } = useSubscription();
  const allowed = hasFeature(feature);

  if (allowed) {
    return children;
  }

  const requiredPlan = FEATURE_LABELS[feature] || "Pro";
  const tooltip = `Disponible en ${requiredPlan}`;

  const openUpgrade = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setUpgradeFor({
      feature,
      requiredPlan,
      currentPlan: sub?.planLabel || "tu plan actual",
    });
  };

  // Si es un elemento (Button, Link), lo clonamos con disabled + onClick override
  if (isValidElement(children)) {
    const childCls = (children.props.className || "") + " feature-locked";
    return (
      <span
        title={tooltip}
        className={`relative inline-block ${className}`}
        data-feature-lock={feature}
        data-testid={`feature-lock-${feature}`}
      >
        {cloneElement(children, {
          onClick: openUpgrade,
          disabled: false, // queremos que el click abra modal, no que no haga nada
          className: `${childCls} opacity-60 cursor-not-allowed hover:opacity-80`,
          "data-locked": "true",
        })}
        {showLockIcon && (
          <Lock
            className="absolute -top-1 -right-1 w-3 h-3 text-amber-600 bg-white rounded-full p-0.5 shadow"
            strokeWidth={2.5}
          />
        )}
      </span>
    );
  }
  // Render por defecto: span con tooltip
  return (
    <span
      title={tooltip}
      onClick={openUpgrade}
      className={`inline-flex items-center gap-1 opacity-60 cursor-not-allowed ${className}`}
      data-feature-lock={feature}
      data-testid={`feature-lock-${feature}`}
    >
      {children}
      {showLockIcon && <Lock className="w-3 h-3 text-amber-600" strokeWidth={2.5} />}
    </span>
  );
}
